import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { USER_HOME_PATH } from './constants/userRoutes';
import { ThemeProvider } from './contexts/ThemeContext';
import { ImageCacheProvider } from './contexts/ImageCacheContext';
import { RealtimeProvider } from './realtime';
import NotificationToastBridge from './components/notificacoes/NotificationToastBridge';
import AlertaHost from './components/shared/AlertaHost';
import Layout from './components/Layout';
import UserLayout from './components/user/UserLayout';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import PageLoader from './components/PageLoader';
import RouteMeta from './components/RouteMeta';
import { reportarErro } from './utils/reportarErro';

// Rotas carregadas sob demanda: a landing/login não baixa o dashboard inteiro.
const CadastroClientes = React.lazy(() => import('./pages/CadastroClientes'));
const CadastroServicos = React.lazy(() => import('./pages/CadastroServicos'));
const CadastroPedidos = React.lazy(() => import('./pages/CadastroPedidos'));
const Login = React.lazy(() => import('./pages/Login'));
const ResetarSenha = React.lazy(() => import('./pages/ResetarSenha'));
const UserPage = React.lazy(() => import('./pages/UserPage'));
const UserOrdersPage = React.lazy(() => import('./pages/user/UserOrdersPage'));
const UserSettings = React.lazy(() => import('./pages/user/UserSettings'));
const UserPayments = React.lazy(() => import('./pages/user/UserPayments'));
const UserDashboard = React.lazy(() => import('./pages/user/UserDashboard'));
const UserHelp = React.lazy(() => import('./pages/user/UserHelp'));
const KnowledgeBase = React.lazy(() => import('./pages/user/KnowledgeBase'));
const ArticleView = React.lazy(() => import('./pages/user/ArticleView'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const EditarCliente = React.lazy(() => import('./pages/EditarCliente'));
const DadosCliente = React.lazy(() => import('./pages/DadosCliente'));
const VisualizarPedido = React.lazy(() => import('./pages/VisualizarPedido'));
const EditarPedido = React.lazy(() => import('./pages/EditarPedido'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const Payments = React.lazy(() => import('./pages/Payments'));
const OrcamentoPage = React.lazy(() => import('./pages/OrcamentoPage'));
const BoardsPage = React.lazy(() => import('./features/boards/pages/BoardsListPage'));
const BoardDetail = React.lazy(() => import('./features/boards/pages/BoardPage'));
const CadastroPublico = React.lazy(() => import('./pages/CadastroPublico'));
const CadastrosPendentes = React.lazy(() => import('./pages/CadastrosPendentes'));
const EditarCadastroPendente = React.lazy(() => import('./pages/EditarCadastroPendente'));
const GerenciarOrcamento = React.lazy(() => import('./pages/GerenciarOrcamento'));
const GerenciarLandingPage = React.lazy(() => import('./pages/GerenciarLandingPage'));
const TextConverter = React.lazy(() => import('./pages/TextConverter'));
const InstagramAIPage = React.lazy(() => import('./pages/InstagramAIPage'));
const IndicacoesPage = React.lazy(() => import('./pages/IndicacoesPage'));
const ManutencaoStorage = React.lazy(() => import('./pages/ManutencaoStorage'));
const ArquivosLinkPage = React.lazy(() => import('./pages/ArquivosLinkPage'));
const NotificacoesPage = React.lazy(() => import('./pages/NotificacoesPage'));
const SecurityPage = React.lazy(() => import('./pages/SecurityPage'));
const Configuracoes = React.lazy(() => import('./pages/Configuracoes'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

const App = () => {
  return (
    <ThemeProvider>
      <ImageCacheProvider>
        <RealtimeProvider>
          <NotificationToastBridge />
          <RouteMeta />
          <ErrorBoundary
            onError={(erro, info) =>
              reportarErro(erro, {
                origem: 'render',
                componentStack: info?.componentStack,
              })
            }
          >
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Rotas públicas */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/redefinir-senha/:token" element={<ResetarSenha />} />
                {/* Link canônico do modal de arquivos (redireciona para a página certa) */}
                <Route path="/arquivos/:pedidoId" element={<ArquivosLinkPage />} />
                <Route path="/orcamento" element={<OrcamentoPage />} />
                <Route path="/orcamento/:servico" element={<OrcamentoPage />} />
                <Route path="/cadastro" element={<CadastroPublico />} />

                {/* Rotas do usuário comum */}
                <Route path="/user" element={<UserLayout />}>
                  {/* O painel é a página inicial do cliente: `/user` não é 404. */}
                  <Route index element={<Navigate to={USER_HOME_PATH} replace />} />
                  <Route path=":id" element={<UserPage />} />
                  <Route path="dashboard" element={<UserDashboard />} />
                  <Route path="orders" element={<UserOrdersPage />} />
                  <Route path="payments" element={<UserPayments />} />
                  <Route path="settings" element={<UserSettings />} />
                  <Route path="help" element={<UserHelp />} />
                  <Route path="conhecimento" element={<KnowledgeBase />} />
                  <Route path="conhecimento/:articleId" element={<ArticleView />} />
                  <Route path="pedido/:id" element={<VisualizarPedido />} />
                  <Route path="notificacoes" element={<NotificacoesPage />} />
                </Route>

                {/* Rotas Base de Conhecimento */}
                <Route path="/base-de-conhecimento" element={<KnowledgeBase />} />
                <Route path="/base-de-conhecimento/artigo/:articleId" element={<ArticleView />} />

                {/* Rotas protegidas - Admin */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notificacoes"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <NotificacoesPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cadastro-clientes"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CadastroClientes />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cadastros-pendentes"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CadastrosPendentes />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cadastros-pendentes/editar/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <EditarCadastroPendente />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cadastro-pedidos"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CadastroPedidos />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cadastro-servicos"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CadastroServicos />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/gerenciar-landing-page"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <GerenciarLandingPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/gerenciar-orcamento"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <GerenciarOrcamento />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payments"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Payments />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                {/* Rotas de Quadros */}
                <Route
                  path="/boards"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <BoardsPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/boards/:boardId"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <BoardDetail />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                {/* Outras rotas protegidas */}
                <Route
                  path="/visualizar-pedido/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <VisualizarPedido />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/editar-pedido/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <EditarPedido />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/editar-cliente/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <EditarCliente />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dados-cliente/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <DadosCliente />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/text-converter"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <TextConverter />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/instagram-ai"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <InstagramAIPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/indicacoes"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <IndicacoesPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/manutencao-storage"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ManutencaoStorage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/seguranca"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <SecurityPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/configuracoes"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Configuracoes />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                {/* Qualquer outro caminho é um 404 real (não redireciona silenciosamente) */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </RealtimeProvider>
        <AlertaHost />
      </ImageCacheProvider>
    </ThemeProvider>
  );
};

export default App;
